import { useMemo, useRef, useState } from 'react';
import { Provider } from 'react-redux';
import { Hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConfigProvider, Empty } from 'antd';
import classNames from 'classnames';
import type { AppProps } from 'next/app';
import localFont from 'next/font/local';
import Head from 'next/head';
import { useRouter } from 'next/router';
import NextAdapterApp from 'next-query-params/app';
import NextNProgress from 'nextjs-progressbar';
import { QueryParamProvider } from 'use-query-params';

import { store } from 'store';

import { globalConst, useScrollToTop } from 'modules/global';

import { Footer, Header, HeartFinishModal, IntlProvider, RouteLoader } from 'components';

import 'styles/globals.scss';

const sfUiTextFont = localFont({
  preload: true,
  display: 'swap',
  src: [
    {
      weight: '300',
      style: 'normal',
      path: '../public/font/sf_ui/SFUIText-light.otf'
    },
    {
      weight: '400',
      style: 'normal',
      path: '../public/font/sf_ui/SFUIText-regular.otf'
    },
    {
      weight: '500',
      style: 'normal',
      path: '../public/font/sf_ui/SFUIText-medium.otf'
    },
    {
      weight: '600',
      style: 'normal',
      path: '../public/font/sf_ui/SFUIText-semibold.otf'
    },
    {
      weight: '700',
      style: 'normal',
      path: '../public/font/sf_ui/SFUIText-bold.otf'
    },
    {
      weight: '800',
      style: 'normal',
      path: '../public/font/sf_ui/SFUIText-heavy.otf'
    }
  ]
});

const hiddenFooterRoutes = [
  globalConst.pageNames.OTM_LIST,
  globalConst.pageNames.DTM,
  globalConst.pageNames.DTM_TEST,
  globalConst.pageNames.VIRTUAL,
  globalConst.pageNames.VIRTUAL_TEST,
  `${globalConst.pageNames.PRIVATE_UNIVERSITIES}/[universityId]/[directionName]/test`,
  globalConst.pageNames.PRO,
  globalConst.pageNames.LOGIN,
  `${globalConst.pageNames.TOPIC_TEST}/[subject]/[testId]`,
  globalConst.pageNames.PRO_PAYMENT,
  globalConst.pageNames.NOT_FOUND,
  `${globalConst.pageNames.EXAM}/[universitySlug]`,
  `${globalConst.pageNames.EXAM}/[universitySlug]/[directionSlug]`
];

const hiddenHeaderRoutes = [
  globalConst.pageNames.LOGIN,
  `${globalConst.pageNames.EXAM}/[universitySlug]`,
  `${globalConst.pageNames.EXAM}/[universitySlug]/[directionSlug]`
];

export default function App({ Component, pageProps }: AppProps) {
  const { locale, defaultLocale, pathname, route } = useRouter();
  const currentLocale = locale || defaultLocale || 'uz';
  const mainRef = useRef<HTMLDivElement>(null);

  useScrollToTop(mainRef);

  const antdLocale = useMemo(
    () => globalConst.antdLocales[currentLocale as keyof typeof globalConst.antdLocales],
    [currentLocale]
  );
  const shouldShowFooter = !hiddenFooterRoutes.includes(route);
  const shouldHideHeader = !hiddenHeaderRoutes.includes(route);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: false,
            refetchOnMount: false,
            refetchOnWindowFocus: false
          }
        }
      })
  );

  const isPolicyPage = [
    globalConst.pageNames.PRIVACY_POLICY,
    globalConst.pageNames.LOGIN,
    globalConst.pageNames.CABINET,
    globalConst.pageNames.SAVED_TEST,
    globalConst.pageNames.UNFINISHED
  ].includes(pathname);

  return (
    <>
      <Head>
        <title>Akam</title>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {isPolicyPage && <meta name="robots" content="noindex, follow" />}
        <meta name="yandex-verification" content="72d09595ede0e5e5" />
        <meta name="google-site-verification" content="M5PDUVGToEqdR9aVUIs_Z9mRS7XNTGmm-Yd_SSGNbqI" />
        <link rel="icon" href="/favicon.ico" sizes="32x32" />
      </Head>
      <main
        ref={mainRef}
        id="root"
        className={classNames(
          sfUiTextFont.className,
          ['/pro', '/pro/payment'].includes(pathname)
            ? 'h-screen w-full overflow-x-hidden bg-gradient-to-br from-green-500 to-blue-500'
            : 'h-screen w-full overflow-x-hidden bg-[#F6F7FB]'
        )}
      >
        <ConfigProvider
          locale={antdLocale}
          renderEmpty={() => <Empty />}
          message={{
            className: 'z-[1111111111111111]'
          }}
          theme={{
            token: {
              colorPrimary: globalConst.colors.primary
            }
          }}
        >
          <QueryClientProvider client={queryClient}>
            <Hydrate state={pageProps.dehydratedState}>
              <Provider store={store}>
                <QueryParamProvider adapter={NextAdapterApp}>
                  <IntlProvider messages={pageProps?.messages}>
                    {shouldHideHeader && <Header />}
                    <RouteLoader />
                    <NextNProgress options={{ showSpinner: false }} color={globalConst.colors.primary} />
                    <Component {...pageProps} />
                    {shouldShowFooter && <Footer />}
                    <HeartFinishModal />
                    <ReactQueryDevtools initialIsOpen={false} />
                  </IntlProvider>
                </QueryParamProvider>
              </Provider>
            </Hydrate>
          </QueryClientProvider>
        </ConfigProvider>
      </main>
    </>
  );
}
